import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, Lock, Loader2, Crown, Sparkles, Zap, MessageSquare, Heart, Infinity as InfinityIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import QuickNav from '@/components/QuickNav';

import { API } from '@/lib/api';

const AnalysisForm = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingQuota, setCheckingQuota] = useState(true);
  const [quota, setQuota] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [formData, setFormData] = useState({
    total_value: '',
    will_installment: 'no',
    installment_count: '',
    installment_value: '',
    monthly_income: '',
    free_text: ''
  });

  useEffect(() => {
    if (user) {
      checkQuota();
    } else {
      setCheckingQuota(false);
    }
  }, [user]);

  const checkQuota = async () => {
    try {
      const response = await axios.get(`${API}/analysis/quota`, { withCredentials: true });
      setQuota(response.data);
    } catch (error) {
      console.error('Error checking quota:', error);
    } finally {
      setCheckingQuota(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInstallmentChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      will_installment: value,
      installment_count: value === 'no' ? '' : prev.installment_count,
      installment_value: value === 'no' ? '' : prev.installment_value
    }));
  };

  const validateForm = () => {
    // Se tem texto livre, aceita apenas isso
    if (formData.free_text && formData.free_text.trim().length > 10) {
      return true;
    }
    
    // Se não tem texto livre, valida as 3 perguntas
    if (!formData.total_value || parseFloat(formData.total_value) <= 0) {
      toast.error('Informe quanto você vai gastar ou descreva sua situação no campo de texto');
      return false;
    }
    if (formData.will_installment === 'yes') {
      if (!formData.installment_count || parseInt(formData.installment_count) <= 0) {
        toast.error('Informe quantas parcelas');
        return false;
      }
      if (!formData.installment_value || parseFloat(formData.installment_value) <= 0) {
        toast.error('Informe o valor da parcela');
        return false;
      }
    }
    if (!formData.monthly_income || parseFloat(formData.monthly_income) <= 0) {
      toast.error('Informe sua renda mensal ou descreva sua situação no campo de texto');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is logged in
    if (!user) {
      toast.info('Faça login para continuar');
      login();
      return;
    }

    // Check quota
    if (quota && !quota.can_analyze) {
      setShowUpgradeModal(true);
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    try {
      let decision_description = '';
      
      // Se tem texto livre, usa ele como base
      if (formData.free_text && formData.free_text.trim().length > 10) {
        decision_description = formData.free_text.trim();
        
        // Adiciona dados numéricos se preenchidos
        if (formData.total_value) {
          decision_description += ` | Valor: R$ ${formData.total_value}`;
        }
        if (formData.monthly_income) {
          decision_description += ` | Renda mensal: R$ ${formData.monthly_income}`;
        }
        if (formData.will_installment === 'yes' && formData.installment_count) {
          decision_description += ` | Parcelado em ${formData.installment_count}x de R$ ${formData.installment_value}`;
        }
      } else {
        // Build decision description from simplified form
        decision_description = `Gasto de R$ ${formData.total_value}`;
        if (formData.will_installment === 'yes') {
          decision_description += ` parcelado em ${formData.installment_count}x de R$ ${formData.installment_value}`;
        }
        decision_description += ` | Renda mensal: R$ ${formData.monthly_income}`;
      }

      const response = await axios.post(`${API}/analysis/create`, {
        decision_description: decision_description,
        total_value: formData.total_value ? parseFloat(formData.total_value) : 0,
        monthly_income: formData.monthly_income ? parseFloat(formData.monthly_income) : 0,
        involves_debt: formData.will_installment === 'yes',
        installments: formData.will_installment === 'yes' 
          ? `${formData.installment_count} parcelas de R$ ${formData.installment_value}`
          : null,
        biggest_fear: formData.free_text ? 'Análise personalizada' : 'Análise simplificada'
      }, {
        withCredentials: true
      });

      toast.success('Análise concluída!');
      navigate(`/result/${response.data.analysis_id}`);
    } catch (error) {
      console.error('Analysis error:', error);
      
      if (error.response?.status === 403) {
        const detail = error.response.data.detail;
        if (detail?.upgrade_required) {
          setShowUpgradeModal(true);
        } else {
          toast.error(detail?.message || 'Você atingiu o limite de análises');
        }
      } else if (error.response?.status === 401) {
        toast.error('Faça login para continuar');
        login();
      } else {
        toast.error('Erro ao processar análise. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show login prompt if not authenticated
  if (!user && !checkingQuota) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" className="text-gray-400 hover:text-white ml-12 sm:ml-14" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
              </Button>
              <div className="flex items-center gap-2">
                <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
                <span className="font-bold text-white">Evite Dívidas</span>
              </div>
            </div>
          </div>
        </nav>

        <main className="pt-24 pb-16 px-4">
          <div className="max-w-lg mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="w-20 h-20 rounded-full bg-[#00FF94]/20 flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-[#00FF94]" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Faça Login para Continuar</h1>
              <p className="text-gray-400 mb-8">
                Faça login com Google para acessar sua <span className="text-[#00FF94] font-semibold">análise gratuita</span> e proteger suas finanças.
              </p>
              
              <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6 mb-8">
                <h3 className="text-white font-semibold mb-4">O que você ganha:</h3>
                <ul className="space-y-3 text-left">
                  <li className="flex items-center gap-3 text-gray-300">
                    <Sparkles className="w-5 h-5 text-[#00FF94]" />
                    1 análise gratuita para começar
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <Zap className="w-5 h-5 text-[#00FF94]" />
                    Classificação de risco com IA
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <Shield className="w-5 h-5 text-[#00FF94]" />
                    Alertas e alternativas seguras
                  </li>
                </ul>
              </div>

              <Button 
                size="lg"
                className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold px-8 py-6 glow-primary"
                onClick={() => login()}
                data-testid="login-to-analyze-btn"
              >
                Entrar com Google
              </Button>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }

  if (checkingQuota) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              className="text-gray-400 hover:text-white ml-12 sm:ml-14"
              onClick={() => navigate('/')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-2">
              <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
              <span className="font-bold text-white">Evite Dívidas</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Form Section */}
      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Quota Status Banner - Credits */}
            {quota && (
              <div className={`mb-6 p-4 rounded-xl border ${
                quota.is_unlimited 
                  ? 'bg-[#00FF94]/10 border-[#00FF94]/30' 
                  : quota.creditos_ia > 0 
                    ? 'bg-[#0A0A0A] border-white/10' 
                    : 'bg-red-500/10 border-red-500/30'
              }`} data-testid="quota-banner">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {quota.is_trial && <Sparkles className="w-4 h-4 text-[#00FF94]" />}
                    {quota.subscription_type === 'premium' && <Crown className="w-4 h-4 text-amber-400" />}
                    {!quota.is_trial && quota.subscription_type !== 'premium' && <Shield className="w-4 h-4 text-gray-400" />}
                    <span className="text-white text-sm font-medium">
                      {quota.is_trial 
                        ? `Trial (${quota.trial_days_remaining} dias restantes)`
                        : quota.plano_ativo === 'premium' 
                          ? 'Plano Premium'
                          : quota.plano_ativo === 'ia_basico'
                            ? 'Plano IA Básico'
                            : 'Plano Gratuito'}
                    </span>
                  </div>
                  {!quota.is_unlimited ? (
                    <div className="flex items-center gap-1" data-testid="hearts-counter" title={`${quota.creditos_ia} de ${quota.creditos_ia_max || 5} consultas de IA restantes`}>
                      {Array.from({ length: quota.creditos_ia_max || 5 }).map((_, i) => (
                        <Heart
                          key={i}
                          className={`w-5 h-5 transition-all ${i < quota.creditos_ia ? 'text-red-500 fill-red-500' : 'text-gray-700'}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-[#00FF94] text-sm font-semibold" data-testid="hearts-unlimited" title="Vidas Infinitas">
                      <Heart className="w-5 h-5 fill-[#00FF94] text-[#00FF94]" />
                      <InfinityIcon className="w-5 h-5" />
                      <span className="hidden sm:inline">Vidas Infinitas</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="text-center mb-10">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Descubra se essa decisão pode virar dívida
              </h1>
              <p className="text-gray-400 text-lg">
                Leva menos de 1 minuto.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Question 1 - Quanto vai gastar */}
              <motion.div 
                className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Label className="text-white text-lg font-semibold mb-3 block">
                  1. Quanto você vai gastar?
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                  <Input
                    type="number"
                    name="total_value"
                    value={formData.total_value}
                    onChange={handleInputChange}
                    placeholder="0,00"
                    className="bg-[#121212] border-white/10 text-white placeholder:text-gray-600 pl-12 focus:border-[#00FF94] text-lg py-6"
                    data-testid="value-input"
                  />
                </div>
              </motion.div>

              {/* Question 2 - Vai parcelar */}
              <motion.div 
                className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <Label className="text-white text-lg font-semibold mb-4 block">
                  2. Você vai parcelar?
                </Label>
                <RadioGroup 
                  value={formData.will_installment} 
                  onValueChange={handleInstallmentChange}
                  className="flex gap-8"
                >
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="yes" id="yes" className="border-white/30 text-[#00FF94] w-5 h-5" data-testid="installment-yes" />
                    <Label htmlFor="yes" className="text-gray-300 cursor-pointer text-lg">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="no" id="no" className="border-white/30 text-[#00FF94] w-5 h-5" data-testid="installment-no" />
                    <Label htmlFor="no" className="text-gray-300 cursor-pointer text-lg">Não</Label>
                  </div>
                </RadioGroup>

                {/* Conditional: Parcelas details */}
                {formData.will_installment === 'yes' && (
                  <motion.div 
                    className="mt-6 space-y-4 pt-4 border-t border-white/10"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                  >
                    <div>
                      <Label className="text-gray-300 mb-2 block">Quantas parcelas?</Label>
                      <Input
                        type="number"
                        name="installment_count"
                        value={formData.installment_count}
                        onChange={handleInputChange}
                        placeholder="Ex: 12"
                        className="bg-[#121212] border-white/10 text-white placeholder:text-gray-600 focus:border-[#00FF94]"
                        data-testid="installment-count-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-2 block">Valor da parcela?</Label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                        <Input
                          type="number"
                          name="installment_value"
                          value={formData.installment_value}
                          onChange={handleInputChange}
                          placeholder="0,00"
                          className="bg-[#121212] border-white/10 text-white placeholder:text-gray-600 pl-12 focus:border-[#00FF94]"
                          data-testid="installment-value-input"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {/* Question 3 - Renda mensal */}
              <motion.div 
                className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Label className="text-white text-lg font-semibold mb-3 block">
                  3. Quanto você ganha por mês?
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                  <Input
                    type="number"
                    name="monthly_income"
                    value={formData.monthly_income}
                    onChange={handleInputChange}
                    placeholder="0,00"
                    className="bg-[#121212] border-white/10 text-white placeholder:text-gray-600 pl-12 focus:border-[#00FF94] text-lg py-6"
                    data-testid="income-input"
                  />
                </div>
              </motion.div>

              {/* Separador OU */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-white/10"></div>
                <span className="text-gray-500 text-sm">ou descreva sua situação</span>
                <div className="flex-1 h-px bg-white/10"></div>
              </div>

              {/* Campo de texto livre */}
              <motion.div 
                className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Label className="text-white text-lg font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#00FF94]" />
                  Conte mais sobre sua situação
                </Label>
                <p className="text-gray-500 text-sm mb-4">
                  Pode ser sobre qualquer decisão financeira: compra, investimento, empréstimo, dúvida...
                </p>
                <Textarea
                  name="free_text"
                  value={formData.free_text}
                  onChange={handleInputChange}
                  placeholder="Ex: Estou pensando em financiar um carro de R$ 50.000 em 60x, meu salário é R$ 4.000 e já tenho um financiamento de moto..."
                  className="bg-[#121212] border-white/10 text-white placeholder:text-gray-600 focus:border-[#00FF94] min-h-[120px] resize-none"
                  data-testid="free-text-input"
                />
              </motion.div>

              {/* Privacy message */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="flex items-center justify-center gap-2 text-gray-500 text-sm"
              >
                <Lock className="w-4 h-4" />
                <span>Seus dados não são armazenados.</span>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {quota && !quota.is_unlimited && quota.creditos_ia <= 0 ? (
                  <Button 
                    type="button"
                    size="lg"
                    className="w-full bg-amber-500 text-black hover:bg-amber-600 rounded-xl font-bold py-6 text-lg transition-all"
                    onClick={() => setShowUpgradeModal(true)}
                    data-testid="credits-exhausted-btn"
                  >
                    <Lock className="w-5 h-5 mr-2" />
                    Corações esgotados - Liberar Vidas Infinitas
                  </Button>
                ) : (
                  <Button 
                    type="submit"
                    size="lg"
                    className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-xl font-bold py-6 text-lg hover:scale-[1.02] transition-all"
                    disabled={loading}
                    data-testid="analyze-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Analisando...
                      </>
                    ) : (
                      'Fazer análise'
                    )}
                  </Button>
                )}
              </motion.div>
            </form>
          </motion.div>
        </div>
      </main>

      {/* Upgrade Modal - Paywall when credits exhausted */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500 fill-red-500" />
              Seus corações acabaram!
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Você usou seus {quota?.creditos_ia_max || 5} corações (consultas de IA) deste mês.
              Eles renovam no próximo mês, ou libere <span className="text-[#00FF94] font-semibold">Vidas Infinitas</span> agora!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* IA Basico Option */}
            {quota?.plano_ativo === 'gratis' && (
              <div 
                className="border border-white/10 rounded-xl p-4 hover:border-[#00FF94]/30 transition-all cursor-pointer"
                onClick={() => navigate('/pricing')}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-white">IA Básico</span>
                  <span className="text-[#00FF94] font-bold text-sm">R$ 9,90/mês</span>
                </div>
                <p className="text-xs text-gray-400">5 créditos de IA renovados mensalmente</p>
              </div>
            )}

            {/* Premium Option */}
            <div 
              className="border border-amber-400/30 rounded-xl p-4 bg-amber-500/5 hover:border-amber-400/50 transition-all cursor-pointer relative"
              onClick={() => navigate('/pricing')}
            >
              <div className="absolute -top-2 right-3 bg-amber-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full">
                MELHOR
              </div>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-400" />
                  <span className="font-bold text-white">Premium</span>
                  <span className="flex items-center gap-0.5 text-[#00FF94] text-xs font-semibold"><Heart className="w-3 h-3 fill-[#00FF94] text-[#00FF94]" /><InfinityIcon className="w-3 h-3" /></span>
                </div>
                <span className="text-amber-400 font-bold text-sm">R$ 29,90/mês</span>
              </div>
              <p className="text-xs text-gray-400">Vidas Infinitas (IA ilimitada) + Organizador completo + Tudo</p>
            </div>

            <div className="flex flex-col gap-3">
              <Button 
                className="w-full bg-gradient-to-r from-[#00FF94] to-emerald-500 text-black font-bold rounded-xl py-5"
                onClick={() => navigate('/pricing')}
                data-testid="paywall-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Ver Todos os Planos
              </Button>
              <Button 
                variant="ghost"
                className="text-gray-400 hover:text-white text-sm"
                onClick={() => setShowUpgradeModal(false)}
              >
                Continuar com plano atual
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Navigation */}
      <QuickNav />
    </div>
  );
};

export default AnalysisForm;
